import isEmpty from "lodash/isEmpty";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as NotesIconSvg } from "../../../../../assets/images/notes-icon.svg";
import {
  MuiBox,
  MuiButtonPrimary,
  MuiButtonTertiary,
  MuiTypography,
} from "../../../../../components";
import { PAGE_URLS } from "../../../../../helpers/constants";
import {
  getValueOrZero,
  replaceHashWithValue,
} from "../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { TransferParticipantResult } from "../../../../createBulkTransfer/modelsAndSchemas/Models";
import { ParticipantData } from "../../../modelsAndSchemas/Models";
import { bulkActivitySiteCoreKeys } from "../../../siteCore/SiteCoreKeys";
import { SummaryList } from "../../summaryList/SummaryList";
import { BulkSummaryDetailWrapper } from "./BulkSummary.style";
import BulkSummaryResult from "./bulkSummaryResult/BulkSummaryResult";

export interface BulkSummaryDetailsProps {
  currentStep: number;
  summaryData?: ParticipantData | TransferParticipantResult;
  onAddBulkActivity: () => void;
  pageId: string;
  transferType?: string;
  isFromBulkTransfer?: boolean;
}

export const BulkSummaryDetails: React.FC<BulkSummaryDetailsProps> = ({
  currentStep,
  summaryData,
  onAddBulkActivity,
  pageId,
  transferType = "",
  isFromBulkTransfer = false,
}) => {
  const { fetchLabel, fetchMediaListImage } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const navigate = useNavigate();
  const [viewModal, setViewModal] = useState(false);
  const onViewSummary = () => {
    setViewModal(true);
  };
  const getLabel = (key: string) => {
    return fetchLabel(bulkActivitySiteCoreKeys.addBulkActivitySummary, key);
  };
  const isUnProcessed =
    getValueOrZero(summaryData?.unProcessedParticipants) === 0;
  const successImage = fetchMediaListImage(
    bulkActivitySiteCoreKeys.summaryTable,
    bulkActivitySiteCoreKeys.tickImage,
    bulkActivitySiteCoreKeys.smallImage,
  );
  const onImDoneClick = () => {
    navigate(`${PAGE_URLS.SEARCH_PROGRAM}`);
  };
  const isFromTransferFlow = !isEmpty(transferType);
  return (
    <BulkSummaryDetailWrapper>
      {isUnProcessed ? (
        <MuiBox>
          <MuiBox className="success-container">
            {!isFromBulkTransfer && (
              <img
                src={successImage?.ImageUrl}
                alt={successImage?.AltText}
                className="success-image"
              />
            )}
          </MuiBox>
          <MuiTypography
            variant="h2SemiBold"
            component="h2"
            className="success-label"
          >
            {isFromTransferFlow
              ? getLabel(
                  bulkActivitySiteCoreKeys.bulkTransferParticipantsSuccessMessage,
                )
              : getLabel(
                  bulkActivitySiteCoreKeys.addBulkActivitySuccessMessage,
                )}
          </MuiTypography>
        </MuiBox>
      ) : (
        <MuiTypography
          data-testid="unprocessed-participants-label"
          variant="h3"
          className="label"
          component="h3"
        >
          {replaceHashWithValue(
            getLabel(bulkActivitySiteCoreKeys.unProcessedParticipantsMessage),
            `${getValueOrZero(summaryData?.unProcessedParticipants)}`,
          )}
        </MuiTypography>
      )}
      <MuiBox className="summary-list-container">
        <SummaryList
          totalCount={getValueOrZero(summaryData?.totalParticipants)}
          processedCount={getValueOrZero(summaryData?.processedParticipants)}
          unprocessedCount={getValueOrZero(
            summaryData?.unProcessedParticipants,
          )}
          {...(!isFromTransferFlow && {
            activitiesCount: getValueOrZero(
              (summaryData as ParticipantData)?.activitites,
            ),
          })}
          isFromTransferFlow={isFromTransferFlow}
        />
      </MuiBox>
      <MuiBox className="button-center-wrapper">
        <MuiButtonTertiary
          className="button-content"
          data-testid={"view-summary-button-testId"}
          onClick={onViewSummary}
          aria-label={commonARIALabel.viewNotes}
        >
          <NotesIconSvg />
          {getLabel(bulkActivitySiteCoreKeys.viewSummary)}
        </MuiButtonTertiary>
      </MuiBox>
      <MuiBox className="dot-container" />
      <MuiBox className="done-button-container">
        <MuiButtonPrimary
          onClick={onImDoneClick}
          data-testid={"done-button-testId"}
        >
          {getLabel(bulkActivitySiteCoreKeys.imDoneButton)}
        </MuiButtonPrimary>
      </MuiBox>
      <MuiBox className="footer-button">
        <MuiButtonTertiary
          onClick={onAddBulkActivity}
          data-testid={"add-bulk-testid"}
        >
          {isFromTransferFlow
            ? getLabel(bulkActivitySiteCoreKeys.addBulkTransfer)
            : getLabel(bulkActivitySiteCoreKeys.addBulkActivityLink)}
        </MuiButtonTertiary>
      </MuiBox>
      {viewModal && (
        <BulkSummaryResult
          open={viewModal}
          setShowViewSummaryModal={setViewModal}
          summaryData={
            isFromTransferFlow
              ? (summaryData as ParticipantData)
              : (summaryData as TransferParticipantResult)
          }
          currentStep={currentStep}
          pageId={pageId}
          transferType={transferType}
        />
      )}
    </BulkSummaryDetailWrapper>
  );
};
